// ─────────────────────────────────────────────────────────────────────────────
// RightPanel.jsx  —  Detail / Chat / Alerts / Messages right column
//
// Used on both desktop (fixed 320px right column) and mobile (full-screen view).
// Contains 4 sub-components defined in this file for easy co-location:
//   VehicleDetailCard — load stats, progress bar, actions, history
//   AlertsList        — system alert cards
//   MessagesList      — driver message preview list
//   ChatPanel         — full dispatcher ↔ driver chat
//
// WHAT TO EDIT HERE:
//   Tab labels     → find the ["detail","chat","alerts","messages"] array
//   Chat bubbles   → find bg-gray-100 (dispatcher) and bg-blue-600 (driver)
//   Action buttons → find the grid-cols-4 action row inside VehicleDetailCard
//   Alert icons    → find ⚠️ and ℹ️ in AlertsList
//   History dots   → find HISTORY.map in VehicleDetailCard
//
// PROPS:
//   rightTab       {string}   "detail" | "chat" | "alerts" | "messages"
//   setRightTab    {fn}
//   selectedLoad   {object}
//   selectedDriver {object}
//   detailOpen     {boolean}
//   setDetailOpen  {fn}
//   simRunning     {boolean}
//   simSpeed       {number}
//   openGPSRecall  {fn}
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useRef, useEffect } from "react";
import { ALERTS, MESSAGES_LIST, CHAT_INIT, AUTO_REPLIES, HISTORY, tnow } from "./constants";

// ── Shared status pill ────────────────────────────────────────────────────────
function StatusPill({ status }) {
  const map = {
    "In Progress": "bg-blue-100 text-blue-700",
    "Pending":     "bg-orange-100 text-orange-600",
    "Completed":   "bg-green-100 text-green-700",
    "Delayed":     "bg-red-100 text-red-600",
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded font-semibold whitespace-nowrap ${map[status] || "bg-gray-100 text-gray-600"}`}>
      {status}
    </span>
  );
}

// ── Vehicle Detail Card ───────────────────────────────────────────────────────
function VehicleDetailCard({ load, driver, simRunning, simSpeed, onClose, onGPSRecall }) {
  if (!load) return <div className="p-4 text-sm text-gray-400">Select a load to view details.</div>;
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm mx-3 mt-3 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <span className="font-semibold text-sm text-gray-800">Vehicle Detail</span>
        <div className="flex items-center gap-1">
          <button className="w-7 h-7 hover:bg-gray-100 rounded-lg flex items-center justify-center text-gray-400">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
            </svg>
          </button>
          <button onClick={onClose} className="w-7 h-7 hover:bg-gray-100 rounded-lg flex items-center justify-center text-gray-400 text-xl leading-none">×</button>
        </div>
      </div>
      <div className="p-4">
        {/* Load header */}
        <div className="flex items-start gap-3 mb-3">
          <div className="w-14 h-11 bg-gray-100 rounded-xl flex items-center justify-center text-2xl flex-shrink-0">🚛</div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-bold text-sm text-gray-900">PLU-01-{load.id.slice(-4)}</span>
              <StatusPill status={load.status}/>
            </div>
            <div className="flex items-center gap-1 mt-0.5">
              <span className="text-amber-400 text-xs">★</span>
              <span className="text-xs font-semibold text-gray-700">{driver?.rating || "—"}</span>
              <span className="text-xs text-gray-400 ml-1">{load.driver}</span>
            </div>
            <p className="text-xs text-gray-500 mt-0.5 truncate">{load.origin} → {load.destination}</p>
            <p className="text-xs text-gray-400">ETA {load.eta} · {load.remaining} remaining</p>
          </div>
        </div>
        {/* Progress bar */}
        <div className="mb-3">
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full bg-blue-500 rounded-full transition-all duration-500" style={{width:`${load.progress}%`}}/>
          </div>
          <p className="text-xs text-gray-500 mt-1">{load.progress}%</p>
        </div>
        {/* Action buttons — add/remove icons here */}
        <div className="grid grid-cols-4 gap-2 mb-4">
          {[
            "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z",
            "M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z",
            "M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z",
          ].map((d, i) => (
            <button key={i} className="h-9 bg-gray-50 hover:bg-gray-100 rounded-lg flex items-center justify-center text-gray-500 transition-colors">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d={d}/>
              </svg>
            </button>
          ))}
          <button className="h-9 bg-gray-50 hover:bg-gray-100 rounded-lg flex items-center justify-center text-gray-500 font-bold text-base transition-colors">···</button>
        </div>
        {/* Stats row */}
        <div className="grid grid-cols-4 gap-2 mb-4 py-3 border-t border-b border-gray-100">
          {[
            {lbl:"SPEED",   val: simRunning ? `${simSpeed} km/h` : `${load.speed} km/h`},
            {lbl:"UPDATED", val: load.lastUpdate},
            {lbl:"FUEL",    val: `${load.fuel}%`},
            {lbl:"TEMP",    val: load.temp},
          ].map(s => (
            <div key={s.lbl} className="text-center">
              <p className="text-[9px] text-gray-400 font-medium tracking-wider">{s.lbl}</p>
              <p className="text-xs font-bold text-gray-800 mt-0.5">{s.val}</p>
            </div>
          ))}
        </div>
        {/* History timeline — data from HISTORY in constants.js */}
        <div>
          <div className="flex items-center justify-between mb-2.5">
            <span className="text-sm font-semibold text-gray-800">Recent History</span>
            <button onClick={onGPSRecall} className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors">
              📡 GPS Recall
            </button>
          </div>
          <div className="space-y-2.5">
            {HISTORY.map((h, i) => (
              <div key={i} className="flex items-center gap-3 text-xs">
                <span className="text-gray-400 font-mono text-[10px] w-9 flex-shrink-0">{h.time}</span>
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${h.dot==="green"?"bg-green-500":h.dot==="blue"?"bg-blue-500":h.dot==="amber"?"bg-amber-400":"bg-gray-300"}`}/>
                <span className="font-semibold text-gray-700 w-16 flex-shrink-0">{h.status}</span>
                <span className="text-gray-400 truncate">{h.place}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Alerts List ───────────────────────────────────────────────────────────────
function AlertsList() {
  return (
    <div className="px-3 py-3">
      <div className="flex items-center justify-between mb-2">
        <span className="font-semibold text-sm text-gray-800">Alerts</span>
        <button className="text-xs text-blue-600 font-medium hover:text-blue-800">View all</button>
      </div>
      <div className="space-y-2">
        {ALERTS.map((a, i) => (
          <div key={i} className="flex items-start gap-2.5 bg-white rounded-xl p-3 border border-gray-100">
            <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${a.type==="warn" ? "bg-orange-100" : "bg-blue-100"}`}>
              <span className="text-sm">{a.type === "warn" ? "⚠️" : "ℹ️"}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-gray-800 leading-snug">{a.title}</p>
              <p className="text-xs text-gray-400 mt-0.5">{a.sub}</p>
            </div>
            <span className="text-[10px] text-gray-300 flex-shrink-0 whitespace-nowrap">{a.time}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Messages List ─────────────────────────────────────────────────────────────
function MessagesList({ onOpenChat }) {
  return (
    <div className="px-3 py-3">
      <div className="flex items-center justify-between mb-2">
        <span className="font-semibold text-sm text-gray-800">Messages</span>
        <button className="text-xs text-blue-600 font-medium hover:text-blue-800">View all</button>
      </div>
      <div className="space-y-2">
        {MESSAGES_LIST.map((m, i) => (
          <button key={i} onClick={() => onOpenChat(m)} className="w-full flex items-center gap-2.5 bg-white rounded-xl p-3 border border-gray-100 hover:border-blue-200 transition-colors text-left">
            <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center font-bold text-blue-700 text-sm flex-shrink-0">{m.avatar}</div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-gray-800">{m.name}</p>
              <p className="text-xs text-gray-400 truncate mt-0.5">{m.msg}</p>
            </div>
            <div className="flex flex-col items-end gap-1 flex-shrink-0">
              <span className="text-[10px] text-gray-300">{m.time}</span>
              {m.unread && <span className="w-2 h-2 bg-blue-500 rounded-full"/>}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Chat Panel ────────────────────────────────────────────────────────────────
// Dispatcher bubbles = gray (right side), Driver bubbles = blue (left side)
function ChatPanel({ driver }) {
  const [msgs, setMsgs] = useState(CHAT_INIT);
  const [inp, setInp]   = useState("");
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({behavior:"smooth"}); }, [msgs]);

  const send = () => {
    if (!inp.trim()) return;
    setMsgs(p => [...p, {id:`m${Date.now()}`, from:"d", text:inp.trim(), ts:tnow()}]);
    setInp("");
    setTimeout(() => setMsgs(p => [...p, {
      id:`mr${Date.now()}`, from:"r",
      text: AUTO_REPLIES[Math.floor(Math.random() * AUTO_REPLIES.length)],
      ts: tnow(),
    }]), 800 + Math.random() * 600);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Chat header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 bg-gray-50 flex-shrink-0">
        <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center font-bold text-blue-700 text-sm flex-shrink-0">
          {driver?.avatar || "?"}
        </div>
        <div>
          <p className="text-sm font-semibold text-gray-800">{driver?.name || "Driver"}</p>
          <div className="flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 bg-green-500 rounded-full"/>
            <span className="text-xs text-green-600">Online</span>
          </div>
        </div>
      </div>
      {/* Message bubbles */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {msgs.map(m => (
          <div key={m.id} className={`flex ${m.from==="d" ? "justify-end" : "justify-start"}`}>
            <div className={`flex flex-col ${m.from==="d" ? "items-end" : "items-start"} max-w-[80%]`}>
              {/* gray = dispatcher (sent), blue = driver (received) */}
              <div className={`px-3 py-2 rounded-2xl text-sm leading-relaxed ${
                m.from === "d"
                  ? "bg-gray-100 text-gray-800 rounded-br-sm"
                  : "bg-blue-600 text-white rounded-bl-sm"
              }`}>{m.text}</div>
              <span className="text-[10px] text-gray-300 mt-1 px-1">{m.ts}</span>
            </div>
          </div>
        ))}
        <div ref={endRef}/>
      </div>
      {/* Input */}
      <div className="px-3 py-3 border-t border-gray-100 flex-shrink-0">
        <div className="flex items-center gap-2 border border-gray-200 rounded-xl px-3 py-2 bg-gray-50">
          <span className="text-gray-400 text-lg cursor-pointer">📎</span>
          <input
            value={inp}
            onChange={e => setInp(e.target.value)}
            onKeyDown={e => e.key === "Enter" && send()}
            placeholder="Type a message..."
            className="flex-1 bg-transparent text-sm text-gray-800 placeholder-gray-300 outline-none min-w-0"
          />
          <button
            onClick={send}
            disabled={!inp.trim()}
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-xs px-3 py-1.5 rounded-lg font-semibold transition-colors flex-shrink-0"
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
}

// ── RightPanel (exported) ─────────────────────────────────────────────────────
export default function RightPanel({
  rightTab, setRightTab,
  selectedLoad, selectedDriver,
  detailOpen, setDetailOpen,
  simRunning, simSpeed, openGPSRecall,
}) {
  return (
    <>
      {/* Tab bar — change tab labels in the array below */}
      <div className="flex border-b border-gray-200 bg-white flex-shrink-0 sticky top-0 z-10">
        {["detail","chat","alerts","messages"].map(t => (
          <button key={t} onClick={() => setRightTab(t)}
            className={`flex-1 py-3 text-xs font-semibold capitalize transition-colors border-b-2 ${
              rightTab === t ? "border-blue-600 text-blue-700" : "border-transparent text-gray-400 hover:text-gray-700"
            }`}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {/* Detail tab */}
      {rightTab === "detail" && (
        <div className="overflow-y-auto flex-1">
          {detailOpen && (
            <VehicleDetailCard load={selectedLoad} driver={selectedDriver}
              simRunning={simRunning} simSpeed={simSpeed}
              onClose={() => setDetailOpen(false)} onGPSRecall={openGPSRecall}/>
          )}
          {!detailOpen && (
            <div className="p-3">
              <button onClick={() => setDetailOpen(true)} className="text-sm text-blue-600 hover:underline">
                Show vehicle detail
              </button>
            </div>
          )}
          <AlertsList/>
          <MessagesList onOpenChat={() => setRightTab("chat")}/>
        </div>
      )}

      {/* Chat tab */}
      {rightTab === "chat" && (
        <div className="flex-1 flex flex-col overflow-hidden">
          <ChatPanel driver={selectedDriver}/>
        </div>
      )}

      {/* Alerts tab */}
      {rightTab === "alerts" && (
        <div className="overflow-y-auto flex-1">
          <div className="p-3 space-y-2">
            <h3 className="font-semibold text-sm text-gray-800 mb-3">All Alerts</h3>
            {ALERTS.map((a, i) => (
              <div key={i} className="flex items-start gap-2.5 bg-white rounded-xl p-3 border border-gray-100">
                <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${a.type==="warn"?"bg-orange-100":"bg-blue-100"}`}>
                  <span className="text-sm">{a.type==="warn"?"⚠️":"ℹ️"}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-gray-800">{a.title}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{a.sub}</p>
                </div>
                <span className="text-[10px] text-gray-300 flex-shrink-0">{a.time}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Messages tab */}
      {rightTab === "messages" && (
        <div className="overflow-y-auto flex-1">
          <div className="p-3 space-y-2">
            <h3 className="font-semibold text-sm text-gray-800 mb-3">Messages</h3>
            {MESSAGES_LIST.map((m, i) => (
              <button key={i} onClick={() => setRightTab("chat")} className="w-full flex items-center gap-2.5 bg-white rounded-xl p-3 border border-gray-100 hover:border-blue-200 transition-colors text-left">
                <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center font-bold text-blue-700 text-sm flex-shrink-0">{m.avatar}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-gray-800">{m.name}</p>
                  <p className="text-xs text-gray-400 truncate mt-0.5">{m.msg}</p>
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <span className="text-[10px] text-gray-300">{m.time}</span>
                  {m.unread && <span className="w-2 h-2 bg-blue-500 rounded-full"/>}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
